import { useState } from 'react'
import type { PromoHolderDto } from '@lunary/api-client'
import { Modal } from '@lunary/ui-web/modal'
import { formatNumber } from '../../shared/ui/charts/scale'
import fieldStyles from '../../shared/ui/confirm-dialog.module.css'
import styles from './lunars.module.css'

const LIMIT = 10_000

type Props = {
  readonly holder: PromoHolderDto | null
  readonly pending: boolean
  readonly error: string | null
  readonly onSubmit: (amount: number, comment: string) => void
  readonly onClose: () => void
}

export function PromoDialog({ holder, pending, error, onSubmit, onClose }: Props) {
  const [mode, setMode] = useState<'grant' | 'revoke'>('grant')
  const [amount, setAmount] = useState('')
  const [comment, setComment] = useState('')
  const [current, setCurrent] = useState<string | null>(null)
  if ((holder?.user_id ?? null) !== current) {
    setCurrent(holder?.user_id ?? null)
    setMode('grant')
    setAmount('')
    setComment('')
  }
  const n = Number(amount)
  const max = mode === 'grant' ? LIMIT : Math.min(LIMIT, holder?.balance ?? 0)
  const valid = Number.isInteger(n) && n > 0 && n <= max

  return (
    <Modal
      open={holder !== null}
      size="sm"
      title={mode === 'grant' ? 'Начислить промо-лунары' : 'Списать промо-лунары'}
      description={
        holder
          ? `${holder.display_name ?? 'Таролог'} · сейчас ${formatNumber(holder.balance)}. Промо-лунары идут только на подарки разборов, это не продажа: чек не нужен.`
          : undefined
      }
      dismissible={!pending}
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault()
        if (valid && !pending) onSubmit(mode === 'grant' ? n : -n, comment.trim())
      }}
      footer={
        <>
          <button type="button" className="secondary" disabled={pending} onClick={onClose}>
            Отмена
          </button>
          <button type="submit" disabled={pending || !valid}>
            {pending ? 'Минуту…' : mode === 'grant' ? 'Начислить' : 'Списать'}
          </button>
        </>
      }
    >
      <div role="radiogroup" aria-label="Действие" className={styles.periods}>
        <button type="button" role="radio" aria-checked={mode === 'grant'} className={mode === 'grant' ? styles.periodActive : styles.period} onClick={() => setMode('grant')}>
          Начислить
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={mode === 'revoke'}
          className={mode === 'revoke' ? styles.periodActive : styles.period}
          disabled={(holder?.balance ?? 0) <= 0}
          onClick={() => setMode('revoke')}
        >
          Списать
        </button>
      </div>
      <label className={fieldStyles.field}>
        <span className="muted">Сколько лунаров (до {formatNumber(max)})</span>
        <input type="number" inputMode="numeric" min={1} max={max} step={1} value={amount} autoFocus onChange={(e) => setAmount(e.target.value)} />
      </label>
      <label className={fieldStyles.field}>
        <span className="muted">Комментарий: акция, канал, период</span>
        <input value={comment} maxLength={200} placeholder="Например, розыгрыш в Telegram-канале" onChange={(e) => setComment(e.target.value)} />
      </label>
      {error && (
        <p className="error-box" role="alert">
          {error}
        </p>
      )}
    </Modal>
  )
}
